import React from "react";
import PropTypes from "prop-types";
import { GroupRounded } from "@mui/icons-material";
import styled from "styled-components";
import { styled as materialStyled } from "@mui/material/styles";

import { Tr, Td } from "commons";
import { convertToMoney } from "commons/src/Money";
import { InfoPopover, InfoDetail, useInfoPopover } from "commons/src/InfoPopover";

import { Card } from "./Card";
import { FolioTable } from "./FolioTable";
import {
    getDescription,
    hasDescription,
    labels,
    pages
} from "../../components/AccountDetailDescription";

export function AccountSummary({
    isJointAccount,
    jointHolderName,
    accountNumber,
    accountStatus,
    transactions,
    effectiveDateRange: { startDate, valuationDate } = {}
}) {
    const [anchorElement, popoverContent, openInfoPopover, closeInfoPopover] = useInfoPopover();

    return (
        <Card>
            <InfoPopover
                handleClose={closeInfoPopover}
                anchorEl={anchorElement}
                content={popoverContent}
            />
            <FolioTable dense data-testid="account-summary-table">
                <tbody>
                    {isJointAccount && (
                        <Tr
                            aria-describedby="account-holders"
                            data-testid="account-summary-table-row"
                        >
                            <Td align="left">
                                <span id="account-holders">Joint account holders </span>
                            </Td>
                            <Td align="right">
                                <JointHolderContainer>
                                    <JointAccountIcon
                                        fontSize="small"
                                        color="primary"
                                        data-testid="joint-account-icon"
                                    />
                                    {jointHolderName}
                                </JointHolderContainer>
                            </Td>
                        </Tr>
                    )}

                    {transactions.map(lv => (
                        <Tr
                            key={lv.label}
                            aria-describedby={lv.label}
                            data-testid="account-summary-table-row"
                        >
                            <Td align="left">
                                {hasDescription(lv.label) ? (
                                    <InfoDetail
                                        onClick={e =>
                                            openInfoPopover(
                                                e,
                                                getDescription(
                                                    lv.label,
                                                    lv.label === labels.gainLoss
                                                        ? {
                                                              startDate,
                                                              endDate: valuationDate,
                                                              accountNumber,
                                                              currentPage: pages.accountDetails
                                                          }
                                                        : null
                                                )
                                            )
                                        }
                                    >
                                        <span id={lv.label}>{lv.label}</span>
                                    </InfoDetail>
                                ) : (
                                    <span id={lv.label}>{lv.label}</span>
                                )}
                            </Td>
                            <Td align="right" negative={lv.value < 0}>
                                {convertToMoney(lv.value)}
                            </Td>
                        </Tr>
                    ))}

                    {!!accountStatus && (
                        <Tr aria-describedby="status" data-testid="account-summary-table-row">
                            <Td align="left">
                                <span id="status">Status </span>
                            </Td>
                            <Td align="right">{accountStatus}</Td>
                        </Tr>
                    )}

                    <Tr aria-describedby="valuation-as-at" data-testid="account-summary-table-row">
                        <Td align="left">
                            <InfoDetail
                                id="valuation-as-at"
                                onClick={e =>
                                    openInfoPopover(e, getDescription(labels.valuationDate))
                                }
                            >
                                Valuation as at
                            </InfoDetail>
                        </Td>
                        <Td align="right">{valuationDate}</Td>
                    </Tr>
                </tbody>
            </FolioTable>
        </Card>
    );
}

const JointAccountIcon = materialStyled(GroupRounded)({
    marginRight: "0.5rem"
});

const JointHolderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

AccountSummary.propTypes = {
    transactions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired
        })
    ),
    accountNumber: PropTypes.string,
    accountStatus: PropTypes.string,
    isJointAccount: PropTypes.bool,
    jointHolderName: PropTypes.string,
    accountStartDate: PropTypes.array,
    effectiveDateRange: PropTypes.shape({
        startDate: PropTypes.string,
        valuationDate: PropTypes.string
    })
};
